import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useFormik } from "formik";
import { find, indexOf, isUndefined, pickBy } from "lodash";
import React, { ChangeEvent, Fragment, MouseEvent, useCallback, useEffect, useState } from "react";
import { Badge, Button, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Nullable } from "../../../types/utils";
import * as Api from '../../api';
import { OrderCategory, OrderCostingVersion, TApiOrder, TApiOrderDetails } from "../../api/endpoints/Order";
import { TApiServiceCharge } from "../../api/endpoints/ServiceCharge";
import { TApiServicePackage } from "../../api/endpoints/ServicePackage";
import { TApiVolumeBand } from "../../api/endpoints/VolumeBand";
import { submitOrder } from "../../store/action-creators/orders";
import ExternalLink from "../Navigation/Links/ExternalLink";
import Footnote from "../RealtimeProductConfigurator/Footnote";
import FormError from "../RealtimeProductConfigurator/FormError";
import OrderCostPreview from "./ServicePackagePicker/OrderCostPreview";
import ServicePackageStep from "./ServicePackageStep";

interface BaseProps {
  // order: TApiOrderDetails;
  orderCostingVersion: OrderCostingVersion;
  onComplete?: () => void;
}

type Props = BaseProps & ({
  order: TApiOrderDetails<OrderCategory.REALTIME | OrderCategory.EU_CUTOUT>;
} | {
  order: TApiOrderDetails<OrderCategory.ARCHIVE | OrderCategory.WEB>;
})

interface IOrderSubmissionFormValues {
  note: Nullable<string>;
  service_package_id: Nullable<number>;
}

type TFormErrorMessage = string | undefined;

type TOrderSubmissionFormErrors = {
  [key in keyof IOrderSubmissionFormValues]?: TFormErrorMessage
}

type TOrderSubmissionStep = {
  id: 'service-package' | 'note' | 'preview';
  title: string;
  enabled: boolean;
}

const STEPS = (order: TApiOrder): Array<TOrderSubmissionStep> => {
  return [
    {
      id: 'service-package',
      title: 'Select Service Package',
      enabled: order.category === OrderCategory.REALTIME || order.category === OrderCategory.EU_CUTOUT
    },
    {
      id: 'note',
      title: 'Add note',
      enabled: true
    },
    {
      id: 'preview',
      title: 'Preview',
      enabled: true
    }
  ]
}

const stepsByOrderCategory = (order: TApiOrder): Array<TOrderSubmissionStep> => {
  return STEPS(order).filter(item => item.enabled);
}

const useSubmissionStep = (order: TApiOrderDetails) => {
  const steps = stepsByOrderCategory(order);
  const stepIds = steps.map(step => step.id);

  const [progress, setProgress] = useState<Array<TOrderSubmissionStep['id']>>([stepIds[0]]);
  const activeStep = progress[progress.length - 1];

  const nextStep = (values: IOrderSubmissionFormValues) => {
    setProgress([...progress, stepIds[indexOf(stepIds, activeStep) + 1]])
  }

  const previousStep = () => {
    setProgress(progress.slice(0, progress.length - 1))
  }

  const resetStep = () => {
    setProgress([stepIds[0]]);
  }

  return { activeStep, nextStep, previousStep, resetStep }
}

const OrderSubmissionForm = (props: Props) => {
  const {
    order,
    orderCostingVersion,
    onComplete
  } = props;
  const dispatch = useDispatch();
  const steps = stepsByOrderCategory(order);
  const stepIds = steps.map(step => step.id);
  const { activeStep, nextStep, previousStep, resetStep } = useSubmissionStep(order);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [servicePackages, setServicePackages] = useState<Array<TApiServicePackage>>([]);
  const [serviceCharges, setServiceCharges] = useState<Array<TApiServiceCharge>>([]);
  const [volumeBands, setVolumeBands] = useState<Array<TApiVolumeBand>>([]);
  const isLastStep = activeStep === steps[steps.length - 1].id;
  const isFirstStep = activeStep === steps[0].id;

  const validateForm = (values: IOrderSubmissionFormValues) => {
    const errors: TOrderSubmissionFormErrors = {};

    if (activeStep === 'service-package') {
      errors.service_package_id = values.service_package_id ? undefined : 'Please, select a service package.'
    }

    return pickBy(errors, v => !isUndefined(v));
  }

  const onSubmit = useCallback((values, actions) => {
    if (isLastStep) {
      dispatch(
        submitOrder(order, {
          status_note: values.note,
          ...(values.service_package_id && { service_package_id: values.service_package_id }),
          // @ts-ignore
        })).then(() => {
          setConfirmed(true);
        }
      );
    } else {
      nextStep(values);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }, [nextStep, dispatch, isLastStep, order]);

  const formik = useFormik<IOrderSubmissionFormValues>({
    initialValues: {
      note: '',
      service_package_id: null
    },
    validate: validateForm,
    onSubmit
  });

  const onSubmitComplete = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    onComplete && onComplete();
  }, [onComplete])

  const onBack = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    previousStep();
  }, [activeStep]);

  const onServicePackageChange = useCallback((value: TApiServicePackage) => {
    formik.setFieldValue('service_package_id', value.id);
  }, [formik]);

  const onNoteChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('note', event.target.value);
  }, [formik]);

  useEffect(() => {
    resetStep();
    Api.VolumeBand.list().then(setVolumeBands);
    Api.ServiceCharge.list({ service_charge_model_id: orderCostingVersion?.service_charge_model_id })
      .then(serviceCharges => {
        setServiceCharges(serviceCharges);

        Api.ServicePackage.list().then(servicePackages => {
          const supportedServicePackageIds = serviceCharges.reduce((acc, sc) => {
            acc.add(sc.service_package_id);

            return acc;
          }, new Set<number>());

          const supportedServicePackages = servicePackages.filter(sp => supportedServicePackageIds.has(sp.id));
          setServicePackages(supportedServicePackages);
        });
      });
  }, [orderCostingVersion]);

  const orderVolumeBand = find(volumeBands, vb => vb.id === order.volume_band_id);
  const chosenServicePackage = find(servicePackages, sp => sp.id === formik.values.service_package_id);
  const chosenServiceCharge = find(serviceCharges, sc => sc.volume_band_id === order.volume_band_id && sc.service_package_id === chosenServicePackage?.id);

  return (
    <Fragment>
      {
        confirmed &&
        <div className="order-submission-confirmation">
          <div className="content">
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faCheckCircle} size="4x" />
            </div>

            <div className="message">
              Your order (ref: {order.id}) has been submitted successfully!
            </div>

            <div className="margin-top-sm">
              <ExternalLink url="https://confluence.ecmwf.int/pages/viewpage.action?pageId=205365051">
                What happens next?
              </ExternalLink>
            </div>
          </div>

          <div className="actions">
            <Button onClick={onSubmitComplete}>
              View order
            </Button>
          </div>
        </div>
      }
      {
        !confirmed &&
        <Container className="order-submission-form">
          <Row>
            <Col xs={8}>
              <Form onSubmit={formik.handleSubmit}>
                <fieldset>
                  {
                    activeStep === 'service-package' && orderVolumeBand &&
                    <ServicePackageStep
                      formik={formik}
                      order={order}
                      volumeBand={orderVolumeBand}
                      serviceCharges={serviceCharges}
                      servicePackages={servicePackages}
                      onServicePackageChange={onServicePackageChange}
                    />
                  }

                  {
                    activeStep === 'note' &&
                    <Fragment>
                      <Form.Group controlId="note">
                        <Form.Label>Add message to licensor</Form.Label>
                        <Form.Control as="textarea" rows={4} onChange={onNoteChange} value={formik.values.note ?? ''} />
                        <FormError message={formik.errors.note}
                                   when={formik.touched.note && Boolean(formik.errors.note)} />
                      </Form.Group>

                      {
                        (order.category === OrderCategory.REALTIME || order.category === OrderCategory.WEB) &&
                        <Footnote
                          text={
                            <Fragment>
                              <p>
                                Please add any additional comments to your order, such as the number of an associated
                                support ticket or if this amends an existing licence.
                              </p>

                              {
                                order.category === OrderCategory.REALTIME &&
                                <p>
                                  If you request a Research Project Fee-Waiver Service Agreement, please use the message box to indicate the project
                                  description, length and participants.
                                </p>
                              }
                            </Fragment>
                          }
                        />
                      }

                      {
                        (order.category === OrderCategory.ARCHIVE) &&
                        <Footnote
                          text={
                            <Fragment>
                              <p>
                                If you request a Research Project Fee-Waiver Service Agreement, please use the message box to indicate the project
                                description, length and participants.
                              </p>
                            </Fragment>
                          }
                        />
                      }
                    </Fragment>
                  }

                  {
                    activeStep === 'preview' &&
                    <OrderCostPreview
                      order={order}
                      serviceCharge={chosenServiceCharge}
                      servicePackage={chosenServicePackage}
                      volumeBand={orderVolumeBand}
                    />
                  }
                </fieldset>

                <div className="actions">
                  {
                    !isFirstStep &&
                    <Button variant="secondary" onClick={onBack}>Back</Button>
                  }

                  <Button type="submit">
                    {
                      isLastStep ? 'Submit' : 'Next'
                    }
                  </Button>
                </div>
              </Form>
            </Col>

            <Col xs={4}>
              <div className="stepper">
                {
                  steps.map((step, index) =>
                    <StepperItem
                      key={step.id}
                      title={step.title}
                      completed={indexOf(stepIds, activeStep) > index}
                      active={activeStep === step.id}
                      number={index + 1}
                    />
                  )
                }
              </div>
            </Col>
          </Row>
        </Container>
      }
    </Fragment>
  )
}

const StepperItem = (props: {
  number: number;
  active: boolean;
  completed: boolean;
  title: string;
}) => {
  const { active, completed, number, title } = props;
  const className = classNames(
    'stepper-item',
    active && 'active',
    completed && 'completed'
  );
  const badgeVariant = active ? 'primary' : 'primary';

  return (
    <div className={className}>
      <div className="indicator">
        {
          completed ?
            <FontAwesomeIcon icon={faCheckCircle} size="lg" />
            : <Badge variant={badgeVariant}>{number}</Badge>

        }
      </div>

      {title}
    </div>

  )
}

export default OrderSubmissionForm;
